import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import EazyLogo from './EazyLogo';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/app';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const PLAN_PRICES: Record<string, number> = { starter: 19, pro: 29, enterprise: 199 };
const PLAN_COLORS: Record<string, string> = {
  trial: '#10b981', starter: '#0ea5e9', pro: '#8b5cf6',
  enterprise: '#f59e0b', free: '#6b7280', cancelled: '#ef4444',
};

interface Org {
  id: string; name: string; plan: string; created_at: string;
  user_count: number; project_count: number;
  stripe_subscription_id: string | null;
  trial_ends_at?: string | null; locked_at?: string | null;
  admin_email?: string | null;
}
interface OrgDetails {
  org: Org;
  users: { id: string; full_name: string; email: string; role: string; created_at: string }[];
  projects: { id: string; name: string; created_at: string }[];
}
interface TrialOrg {
  id: string; name: string; created_at: string; trial_ends_at: string | null;
  admin_email: string | null; admin_name: string | null;
  project_count: number; is_expired: boolean;
}
interface BillingItem {
  id: string; name: string; plan: string;
  next_billing_date: string | null; amount_cents: number | null;
  status: string; cancel_at_period_end: boolean;
}
interface PlanLimit { maxProjects: number | null; maxUsers: number | null; }
interface InvoiceItem {
  id: string; date: string; amount_paid: number; amount_due: number;
  status: string; period_start: string | null; period_end: string | null;
}

interface Props { token: string; onBack: () => void; onLogout: () => void; }

type NavSection = 'dashboard' | 'orgs' | 'billing' | 'settings';

const PLANS = ['all', 'trial', 'starter', 'pro', 'enterprise', 'cancelled'] as const;

const NAV_ITEMS: { id: NavSection; label: string; icon: string }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: '▦' },
  { id: 'orgs',      label: 'Οργανισμοί', icon: '🏢' },
  { id: 'billing',   label: 'Billing', icon: '💳' },
  { id: 'settings',  label: 'Ρυθμίσεις', icon: '⚙️' },
];

export default function SuperAdminPanel({ token, onBack, onLogout }: Props) {
  const { language } = useLanguage();
  const L = (language === 'en' ? enStrings : elStrings).superAdmin;
  const headers = { Authorization: `Bearer ${token}` };

  const [activeNav, setActiveNav] = useState<NavSection>('dashboard');
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDeleteOrg, setPendingDeleteOrg] = useState<Org | null>(null);

  // Drill-down
  const [drillOrg, setDrillOrg] = useState<OrgDetails | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillTab, setDrillTab] = useState<'users' | 'projects'>('users');
  const [changingPlan, setChangingPlan] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState('');

  // Trial modal
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialOrgs, setTrialOrgs] = useState<TrialOrg[]>([]);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialFilter, setTrialFilter] = useState<'all' | 'active' | 'expired'>('all');

  // Billing
  const [billingData, setBillingData] = useState<BillingItem[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);

  // Billing history modal
  const [historyOrg, setHistoryOrg] = useState<BillingItem | null>(null);
  const [historyData, setHistoryData] = useState<InvoiceItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Plan limits
  const [planLimits, setPlanLimits] = useState<Record<string, PlanLimit>>({});
  const [planLimitsLoading, setPlanLimitsLoading] = useState(false);
  const [savingPlan, setSavingPlan] = useState<string | null>(null);
  const [planLimitsSuccess, setPlanLimitsSuccess] = useState('');

  const load = () => {
    setLoading(true);
    axios.get(`${API}/organizations`, { headers })
      .then(r => setOrgs(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadPlanLimits = () => {
    setPlanLimitsLoading(true);
    axios.get(`${API}/organizations/plan-limits`, { headers })
      .then(r => setPlanLimits(r.data || {}))
      .catch(() => {})
      .finally(() => setPlanLimitsLoading(false));
  };

  const loadBilling = () => {
    if (billingLoaded) return;
    setBillingLoading(true);
    axios.get(`${API}/organizations/billing-overview`, { headers })
      .then(r => { setBillingData(r.data || []); setBillingLoaded(true); })
      .catch(() => setBillingLoaded(true))
      .finally(() => setBillingLoading(false));
  };

  const openBillingHistory = async (item: BillingItem) => {
    setHistoryOrg(item);
    setHistoryData([]);
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/organizations/${item.id}/billing-history`, { headers });
      setHistoryData(r.data || []);
    } catch {}
    finally { setHistoryLoading(false); }
  };

  const loadTrials = () => {
    setTrialLoading(true);
    axios.get(`${API}/organizations/trial-accounts`, { headers })
      .then(r => setTrialOrgs(r.data || []))
      .catch(() => {})
      .finally(() => setTrialLoading(false));
  };

  useEffect(() => { load(); loadPlanLimits(); }, []);
  useEffect(() => { if (activeNav === 'billing') loadBilling(); }, [activeNav]);

  // Stats
  const totalUsers = orgs.reduce((s, o) => s + o.user_count, 0);
  const totalProjects = orgs.reduce((s, o) => s + o.project_count, 0);
  const activeSubscriptions = orgs.filter(o => o.stripe_subscription_id).length;
  const trialCount = orgs.filter(o => o.plan === 'trial').length;
  const mrr = orgs.reduce((s, o) => s + (PLAN_PRICES[o.plan] || 0), 0);

  const recentSignups = useMemo(() =>
    [...orgs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 8),
    [orgs]
  );

  const filteredOrgs = useMemo(() => {
    let result = orgs;
    if (planFilter !== 'all') result = result.filter(o => o.plan === planFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(o => o.name.toLowerCase().includes(q) || (o.admin_email || '').toLowerCase().includes(q));
    }
    return result;
  }, [orgs, planFilter, search]);

  const openDrill = async (org: Org) => {
    setDrillLoading(true);
    setDrillTab('users');
    setSelectedNewPlan(org.plan);
    setChangingPlan(false);
    try {
      const r = await axios.get(`${API}/organizations/${org.id}/details`, { headers });
      setDrillOrg(r.data);
    } catch {}
    finally { setDrillLoading(false); }
  };

  const handleChangePlan = async () => {
    if (!drillOrg || !selectedNewPlan) return;
    try {
      await axios.put(`${API}/organizations/${drillOrg.org.id}/plan`, { plan: selectedNewPlan }, { headers });
      setOrgs(prev => prev.map(o => o.id === drillOrg.org.id ? { ...o, plan: selectedNewPlan } : o));
      setDrillOrg(prev => prev ? { ...prev, org: { ...prev.org, plan: selectedNewPlan } } : prev);
      setChangingPlan(false);
    } catch {}
  };

  const handleDelete = (org: Org) => setPendingDeleteOrg(org);
  const confirmDelete = async () => {
    if (!pendingDeleteOrg) return;
    const org = pendingDeleteOrg;
    setPendingDeleteOrg(null);
    setDeletingId(org.id);
    try {
      await axios.delete(`${API}/organizations/${org.id}`, { headers });
      if (drillOrg?.org.id === org.id) setDrillOrg(null);
      load();
    } finally { setDeletingId(null); }
  };

  const savePlanLimit = async (plan: string) => {
    setSavingPlan(plan);
    try {
      await axios.put(`${API}/organizations/plan-limits`, {
        plan,
        maxProjects: planLimits[plan]?.maxProjects ?? null,
        maxUsers: planLimits[plan]?.maxUsers ?? null,
      }, { headers });
      setPlanLimitsSuccess(`✓ ${plan} αποθηκεύτηκε`);
      setTimeout(() => setPlanLimitsSuccess(''), 3000);
    } catch {} finally { setSavingPlan(null); }
  };

  const updateLimitField = (plan: string, field: 'maxProjects' | 'maxUsers', value: string) => {
    const num = value === '' ? null : parseInt(value, 10);
    setPlanLimits(prev => ({ ...prev, [plan]: { ...prev[plan], [field]: (num === null || isNaN(num)) ? null : num } }));
  };

  const filteredTrials = trialOrgs.filter(t => {
    if (trialFilter === 'active') return !t.is_expired;
    if (trialFilter === 'expired') return t.is_expired;
    return true;
  });
  const activeTrialCount = trialOrgs.filter(t => !t.is_expired).length;
  const expiredTrialCount = trialOrgs.filter(t => t.is_expired).length;

  const fmtDate = (d: string) => new Date(d).toLocaleDateString(language === 'en' ? 'en-US' : 'el-GR', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeAgo = (d: string) => {
    const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
    if (days === 0) return 'Σήμερα';
    if (days === 1) return 'Χθες';
    if (days < 7) return `${days} μέρες πριν`;
    if (days < 30) return `${Math.floor(days / 7)} εβδ. πριν`;
    return `${Math.floor(days / 30)} μήνες πριν`;
  };

  const planBadge = (plan: string) => (
    <span style={{
      background: (PLAN_COLORS[plan] || '#6b7280') + '20',
      color: PLAN_COLORS[plan] || '#6b7280',
      padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, textTransform: 'capitalize' as const,
    }}>{plan}</span>
  );

  // ── STAT CARDS ───────────────────────────────────────────────────────────
  const statsBar = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 14, marginBottom: 28 }}>
      {[
        { label: 'Οργανισμοί', value: orgs.length, color: '#4f46e5' },
        { label: 'Χρήστες', value: totalUsers, color: '#0ea5e9' },
        { label: 'Projects', value: totalProjects, color: '#10b981' },
        { label: 'Subscriptions', value: activeSubscriptions, color: '#f59e0b' },
        { label: 'Trials', value: trialCount, color: '#10b981', onClick: () => { setShowTrialModal(true); setTrialFilter('all'); loadTrials(); } },
        { label: 'MRR', value: `€${mrr}`, color: '#8b5cf6', onClick: () => setActiveNav('billing') },
      ].map(({ label, value, color, onClick }: any) => (
        <div key={label} onClick={onClick} style={{
          background: '#fff', borderRadius: 10, padding: '18px 20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.07)', borderLeft: `3px solid ${color}`,
          cursor: onClick ? 'pointer' : 'default',
        }}
          onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLElement).style.boxShadow = '0 3px 12px rgba(0,0,0,0.1)'; }}
          onMouseLeave={e => { if (onClick) (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.07)'; }}
        >
          <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{loading ? '—' : value}</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, fontWeight: 500 }}>{label}</div>
        </div>
      ))}
    </div>
  );

  // ── DASHBOARD SECTION ────────────────────────────────────────────────────
  const sectionDashboard = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* Recent Signups */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', marginBottom: 18 }}>Πρόσφατες Εγγραφές</div>
        <div>
          {recentSignups.map((org, i) => (
            <div key={org.id} onClick={() => { setActiveNav('orgs'); setTimeout(() => openDrill(org), 100); }}
              style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', cursor: 'pointer', borderBottom: i < recentSignups.length - 1 ? '1px solid #f9fafb' : 'none' }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            >
              <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: (PLAN_COLORS[org.plan] || '#6b7280') + '15', color: PLAN_COLORS[org.plan] || '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15 }}>
                {org.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.name}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{timeAgo(org.created_at)}</div>
              </div>
              {planBadge(org.plan)}
            </div>
          ))}
          {recentSignups.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>Δεν υπάρχουν εγγραφές</div>}
        </div>
      </div>

      {/* Plan Distribution */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', marginBottom: 18 }}>Κατανομή Plans</div>
        {(['trial', 'starter', 'pro', 'enterprise', 'cancelled'] as const).map(plan => {
          const count = orgs.filter(o => o.plan === plan).length;
          const pct = orgs.length ? Math.round((count / orgs.length) * 100) : 0;
          return (
            <div key={plan} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span style={{ fontWeight: 600, textTransform: 'capitalize', color: '#374151' }}>{plan}</span>
                <span style={{ color: '#9ca3af' }}>{count} org{count !== 1 ? 's' : ''} · {pct}%</span>
              </div>
              <div style={{ height: 7, background: '#f1f5f9', borderRadius: 999 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: PLAN_COLORS[plan] || '#6b7280', borderRadius: 999, transition: 'width 0.5s' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── ORGS SECTION ─────────────────────────────────────────────────────────
  const sectionOrgs = (
    <div>
      <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Αναζήτηση οργανισμού ή email..."
            style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PLANS.map(p => (
            <button key={p} onClick={() => setPlanFilter(p)} style={{
              padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: planFilter === p ? (PLAN_COLORS[p] || '#4f46e5') : '#f1f5f9',
              color: planFilter === p ? '#fff' : '#6b7280',
            }}>
              {p === 'all' ? 'Όλα' : p.charAt(0).toUpperCase() + p.slice(1)}
              {p !== 'all' && ` (${orgs.filter(o => o.plan === p).length})`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>{L.loading}</div>
      ) : filteredOrgs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Δεν βρέθηκαν αποτελέσματα</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {filteredOrgs.map(org => (
            <div key={org.id} onClick={() => openDrill(org)} style={{
              background: '#fff', borderRadius: 10, padding: '16px 18px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.07)', cursor: 'pointer',
              border: '1px solid #f1f5f9', borderLeft: `4px solid ${PLAN_COLORS[org.plan] || '#6b7280'}`,
              transition: 'box-shadow 0.15s, transform 0.1s',
            }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.07)'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.name}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.admin_email || '—'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginLeft: 8, flexShrink: 0 }}>
                  {planBadge(org.plan)}
                  {org.locked_at && <span style={{ background: '#fee2e2', color: '#ef4444', padding: '1px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>Locked</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#6b7280' }}>
                <span>👥 {org.user_count}</span>
                <span>📁 {org.project_count}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11 }}>{fmtDate(org.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── BILLING SECTION ──────────────────────────────────────────────────────
  const sortedBilling = [...billingData].sort((a, b) => {
    if (!a.next_billing_date) return 1;
    if (!b.next_billing_date) return -1;
    return new Date(a.next_billing_date).getTime() - new Date(b.next_billing_date).getTime();
  });

  const sectionBilling = (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e' }}>Billing Overview</div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 3 }}>
          {billingLoading ? 'Φόρτωση από Stripe...' : `${billingData.length} ενεργές συνδρομές · MRR €${mrr}`}
        </div>
      </div>
      {billingLoading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Φόρτωση από Stripe...</div>
      ) : sortedBilling.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Δεν βρέθηκαν ενεργές συνδρομές</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Οργανισμός', 'Plan', 'Ποσό', 'Επόμενη Χρέωση', 'Κατάσταση', ''].map(h => (
                <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedBilling.map((item, i) => {
              const daysUntil = item.next_billing_date ? Math.ceil((new Date(item.next_billing_date).getTime() - Date.now()) / 86400000) : null;
              const isUrgent = daysUntil !== null && daysUntil <= 7;
              const amount = item.amount_cents != null ? `€${(item.amount_cents / 100).toFixed(2)}` : `€${PLAN_PRICES[item.plan] ?? '—'}`;
              return (
                <tr key={item.id} style={{ borderTop: i > 0 ? '1px solid #f8fafc' : 'none', background: isUrgent ? '#fffbeb' : 'transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = isUrgent ? '#fef3c7' : '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = isUrgent ? '#fffbeb' : 'transparent')}>
                  <td style={{ padding: '14px 20px', fontWeight: 600, color: '#1a1a2e', fontSize: 14 }}>{item.name}</td>
                  <td style={{ padding: '14px 20px' }}>{planBadge(item.plan)}</td>
                  <td style={{ padding: '14px 20px', fontWeight: 700, color: '#8b5cf6', fontSize: 15 }}>{amount}</td>
                  <td style={{ padding: '14px 20px' }}>
                    {item.next_billing_date ? (
                      <div>
                        <div style={{ fontSize: 14, color: isUrgent ? '#d97706' : '#374151', fontWeight: isUrgent ? 700 : 400 }}>
                          {isUrgent && '⚠️ '}{fmtDate(item.next_billing_date)}
                        </div>
                        {daysUntil !== null && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{daysUntil === 0 ? 'Σήμερα' : daysUntil < 0 ? `${Math.abs(daysUntil)} μέρες πριν` : `σε ${daysUntil} μέρες`}</div>}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '14px 20px' }}>
                    {item.cancel_at_period_end
                      ? <span style={{ background: '#fee2e2', color: '#ef4444', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>Ακυρώνεται</span>
                      : <span style={{ background: '#dcfce7', color: '#16a34a', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>Ενεργή</span>}
                  </td>
                  <td style={{ padding: '14px 20px' }}>
                    <button onClick={() => openBillingHistory(item)} title="Ιστορικό πληρωμών"
                      style={{ background: '#f0f4ff', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 15, color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#e0e7ff')}
                      onMouseLeave={e => (e.currentTarget.style.background = '#f0f4ff')}>
                      ℹ
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  // ── SETTINGS SECTION ─────────────────────────────────────────────────────
  const sectionSettings = (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e' }}>{L.planLimitsTitle}</div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 3 }}>{L.planLimitsHint}</div>
      </div>
      {planLimitsLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{L.loading}</div>
      ) : (
        <div style={{ padding: '8px 24px 24px' }}>
          {planLimitsSuccess && <div style={{ margin: '12px 0', color: '#16a34a', fontSize: 13 }}>{planLimitsSuccess}</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {[L.colPlanName, L.colMaxProjects, L.colMaxUsers, ''].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(['trial', 'starter', 'pro', 'enterprise'] as const).map((plan, i) => {
                const lim = planLimits[plan] || { maxProjects: null, maxUsers: null };
                return (
                  <tr key={plan} style={{ borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                    <td style={{ padding: '14px 16px' }}>{planBadge(plan)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <input type="number" min={0} value={lim.maxProjects ?? ''} placeholder="∞"
                        onChange={e => updateLimitField(plan, 'maxProjects', e.target.value)}
                        style={{ width: 90, padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 14, outline: 'none' }} />
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <input type="number" min={0} value={lim.maxUsers ?? ''} placeholder="∞"
                        onChange={e => updateLimitField(plan, 'maxUsers', e.target.value)}
                        style={{ width: 90, padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 14, outline: 'none' }} />
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <button onClick={() => savePlanLimit(plan)} disabled={savingPlan === plan}
                        style={{ padding: '7px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: savingPlan === plan ? 0.6 : 1 }}>
                        {savingPlan === plan ? L.savingBtn : L.saveBtn}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'Inter, sans-serif' }}>

      {/* ── SIDEBAR ── */}
      <div style={{ width: 220, background: '#1a1a2e', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'fixed', top: 0, left: 0, bottom: 0 }}>
        <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <EazyLogo size="md" theme="dark" />
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 600, marginTop: 8, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Super Admin</div>
        </div>
        <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto', minHeight: 0 }}>
          {NAV_ITEMS.map(item => {
            const active = activeNav === item.id;
            return (
              <button key={item.id} onClick={() => setActiveNav(item.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                marginBottom: 2, textAlign: 'left',
                background: active ? 'rgba(79,70,229,0.25)' : 'transparent',
                color: active ? '#a5b4fc' : 'rgba(255,255,255,0.5)',
                fontSize: 14, fontWeight: active ? 600 : 400,
                transition: 'all 0.15s',
              }}
                onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 16, opacity: active ? 1 : 0.7 }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </nav>
        <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={onLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: '#fca5a5', fontSize: 13, textAlign: 'left' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            ⏻ {L.logout}
          </button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ marginLeft: 220, flex: 1, background: '#f0f2f5', overflowY: 'auto', minHeight: '100vh' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 28px' }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>
              {NAV_ITEMS.find(n => n.id === activeNav)?.label}
            </div>
          </div>

          {statsBar}

          {activeNav === 'dashboard' && sectionDashboard}
          {activeNav === 'orgs' && sectionOrgs}
          {activeNav === 'billing' && sectionBilling}
          {activeNav === 'settings' && sectionSettings}
        </div>
      </div>

      {/* ── DRILL-DOWN MODAL ── */}
      {(drillOrg || drillLoading) && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) setDrillOrg(null); }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            {drillLoading ? (
              <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>{L.loading}</div>
            ) : drillOrg ? (
              <>
                <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e', marginBottom: 6 }}>{drillOrg.org.name}</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {planBadge(drillOrg.org.plan)}
                        <span style={{ fontSize: 13, color: '#6b7280' }}>👥 {drillOrg.users.length} χρήστες</span>
                        <span style={{ fontSize: 13, color: '#6b7280' }}>📁 {drillOrg.projects.length} projects</span>
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>Εγγραφή {fmtDate(drillOrg.org.created_at)}</span>
                      </div>
                    </div>
                    <button onClick={() => setDrillOrg(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                    {!changingPlan ? (
                      <button onClick={() => setChangingPlan(true)} style={{ padding: '7px 14px', background: '#f0f9ff', color: '#0ea5e9', border: '1px solid #bae6fd', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>✏️ Αλλαγή Plan</button>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select value={selectedNewPlan} onChange={e => setSelectedNewPlan(e.target.value)}
                          style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none' }}>
                          {['trial', 'starter', 'pro', 'enterprise', 'cancelled'].map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <button onClick={handleChangePlan} style={{ padding: '7px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>✓</button>
                        <button onClick={() => setChangingPlan(false)} style={{ padding: '7px 12px', background: '#f1f5f9', color: '#6b7280', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>✕</button>
                      </div>
                    )}
                    <button onClick={() => { setDrillOrg(null); handleDelete(drillOrg.org); }}
                      style={{ padding: '7px 14px', background: '#fee2e2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      🗑 Διαγραφή
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #f1f5f9', flexShrink: 0, padding: '0 28px' }}>
                  {(['users', 'projects'] as const).map(tab => (
                    <button key={tab} onClick={() => setDrillTab(tab)} style={{
                      padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                      color: drillTab === tab ? '#4f46e5' : '#9ca3af',
                      borderBottom: drillTab === tab ? '2px solid #4f46e5' : '2px solid transparent',
                    }}>
                      {tab === 'users' ? `👥 Χρήστες (${drillOrg.users.length})` : `📁 Projects (${drillOrg.projects.length})`}
                    </button>
                  ))}
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {drillTab === 'users' ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                        <tr>{['Όνομα', 'Email', 'Ρόλος', 'Εγγραφή'].map(h => <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {drillOrg.users.map((u, i) => (
                          <tr key={u.id} style={{ borderTop: i > 0 ? '1px solid #f8fafc' : 'none' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            <td style={{ padding: '12px 20px', fontWeight: 600, color: '#1a1a2e', fontSize: 14 }}>{u.full_name}</td>
                            <td style={{ padding: '12px 20px', color: '#6b7280', fontSize: 13 }}>{u.email}</td>
                            <td style={{ padding: '12px 20px' }}><span style={{ background: u.role === 'admin' ? '#e0e7ff' : '#f1f5f9', color: u.role === 'admin' ? '#4f46e5' : '#6b7280', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{u.role}</span></td>
                            <td style={{ padding: '12px 20px', color: '#9ca3af', fontSize: 12 }}>{fmtDate(u.created_at)}</td>
                          </tr>
                        ))}
                        {drillOrg.users.length === 0 && <tr><td colSpan={4} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Δεν υπάρχουν χρήστες</td></tr>}
                      </tbody>
                    </table>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                        <tr>{['Όνομα Project', 'Δημιουργία'].map(h => <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {drillOrg.projects.map((p, i) => (
                          <tr key={p.id} style={{ borderTop: i > 0 ? '1px solid #f8fafc' : 'none' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                            <td style={{ padding: '12px 20px', fontWeight: 600, color: '#1a1a2e', fontSize: 14 }}>📁 {p.name}</td>
                            <td style={{ padding: '12px 20px', color: '#9ca3af', fontSize: 12 }}>{fmtDate(p.created_at)}</td>
                          </tr>
                        ))}
                        {drillOrg.projects.length === 0 && <tr><td colSpan={2} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Δεν υπάρχουν projects</td></tr>}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* ── DELETE MODAL ── */}
      {pendingDeleteOrg && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400, backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteOrg(null); }}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24 }}>⚠️</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 8px', textAlign: 'center' }}>{L.deleteOrgTitle}</h3>
            <p style={{ fontSize: 14, color: '#374151', textAlign: 'center', margin: '0 0 6px' }}>{L.deleteOrgAbout(pendingDeleteOrg.name)}</p>
            <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', margin: '0 0 28px' }}>{L.deleteOrgWarning}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setPendingDeleteOrg(null)} style={{ flex: 1, padding: '11px', border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: 'transparent', color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{L.cancelBtn}</button>
              <button onClick={confirmDelete} disabled={!!deletingId} style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 8, backgroundColor: '#ef4444', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>{L.confirmDeleteBtn}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── BILLING HISTORY MODAL ── */}
      {historyOrg && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 350, backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) setHistoryOrg(null); }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '22px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17, color: '#1a1a2e' }}>Ιστορικό Πληρωμών</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>{historyOrg.name}</div>
              </div>
              <button onClick={() => setHistoryOrg(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {historyLoading ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>Φόρτωση από Stripe...</div>
              ) : historyData.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>Δεν βρέθηκαν εγγραφές{'\n'}(τα test keys δεν έχουν ιστορικό σε production subscriptions)</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                    <tr>
                      {['Περίοδος', 'Ημ/νία Χρέωσης', 'Ποσό', 'Κατάσταση'].map(h => (
                        <th key={h} style={{ padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((inv, i) => {
                      const isPaid = inv.status === 'paid';
                      const isFailed = inv.status === 'uncollectible' || inv.status === 'void';
                      const statusColor = isPaid ? '#16a34a' : isFailed ? '#ef4444' : '#f59e0b';
                      const statusBg = isPaid ? '#dcfce7' : isFailed ? '#fee2e2' : '#fef3c7';
                      const statusLabel = isPaid ? 'Επιτυχής' : isFailed ? 'Αποτυχία' : inv.status === 'open' ? 'Εκκρεμής' : inv.status;
                      const period = inv.period_start && inv.period_end
                        ? `${fmtDate(inv.period_start)} – ${fmtDate(inv.period_end)}`
                        : '—';
                      return (
                        <tr key={inv.id} style={{ borderTop: i > 0 ? '1px solid #f8fafc' : 'none' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                          <td style={{ padding: '13px 20px', fontSize: 13, color: '#374151' }}>{period}</td>
                          <td style={{ padding: '13px 20px', fontSize: 13, color: '#374151' }}>{fmtDate(inv.date)}</td>
                          <td style={{ padding: '13px 20px', fontWeight: 700, fontSize: 14, color: isPaid ? '#8b5cf6' : '#9ca3af' }}>
                            {isPaid ? `€${(inv.amount_paid / 100).toFixed(2)}` : `€${(inv.amount_due / 100).toFixed(2)}`}
                          </td>
                          <td style={{ padding: '13px 20px' }}>
                            <span style={{ background: statusBg, color: statusColor, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{statusLabel}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── TRIAL MODAL ── */}
      {showTrialModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) setShowTrialModal(false); }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 780, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#1a1a2e' }}>{L.trialModalTitle}</div>
                {!trialLoading && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>{L.trialSummary(activeTrialCount, expiredTrialCount, trialOrgs.length)}</div>}
              </div>
              <button onClick={() => setShowTrialModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
            </div>
            <div style={{ padding: '14px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8, flexShrink: 0 }}>
              {(['all', 'active', 'expired'] as const).map(f => {
                const labels = { all: L.filterAll, active: L.filterActive, expired: L.filterExpired };
                const counts = { all: trialOrgs.length, active: activeTrialCount, expired: expiredTrialCount };
                return (
                  <button key={f} onClick={() => setTrialFilter(f)} style={{ padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: trialFilter === f ? '#10b981' : '#f1f5f9', color: trialFilter === f ? 'white' : '#6b7280' }}>
                    {labels[f]} {!trialLoading && `(${counts[f]})`}
                  </button>
                );
              })}
            </div>
            <div style={{ overflowY: 'auto', flexGrow: 1 }}>
              {trialLoading ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>{L.loading}</div>
              ) : filteredTrials.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>{trialFilter === 'all' ? L.noTrials : L.noResults}</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                    <tr>{[L.colTrialOrg, L.colTrialRegistered, L.colTrialExpiry, L.colTrialProjects, L.colTrialStatus].map(h => <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {filteredTrials.map((org, i) => (
                      <tr key={org.id} style={{ borderTop: i > 0 ? '1px solid #f8fafc' : 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <td style={{ padding: '14px 20px' }}><div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: 14 }}>{org.name}</div><div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{org.admin_email || '—'}</div></td>
                        <td style={{ padding: '14px 20px', color: '#6b7280', fontSize: 13 }}>{fmtDate(org.created_at)}</td>
                        <td style={{ padding: '14px 20px', color: '#374151', fontSize: 13 }}>{org.trial_ends_at ? fmtDate(org.trial_ends_at) : '—'}</td>
                        <td style={{ padding: '14px 20px', color: '#374151', fontSize: 14 }}>{org.project_count}</td>
                        <td style={{ padding: '14px 20px' }}><span style={{ background: org.is_expired ? '#fee2e2' : '#dcfce7', color: org.is_expired ? '#ef4444' : '#16a34a', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{org.is_expired ? L.trialExpired : L.trialActive}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ padding: '16px 28px', borderTop: '1px solid #f1f5f9', background: '#fafafa', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowTrialModal(false)} style={{ padding: '9px 20px', background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>{L.closeBtn}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
